/**
 * AI Interview Practice System - Interview Session Component
 *
 * Copyright (c) 2024 Technical Interviewer Attempt. All Rights Reserved.
 * Proprietary and Confidential. Unauthorized copying, distribution, or use is prohibited.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ChangeEvent,
} from 'react';
import { useAuth } from '../contexts/AuthContext';
import VoiceRecorder from './VoiceRecorder';
import ScreenShare, { ScreenShareHandle } from './ScreenShare';
import ChatDisplay from './ChatDisplay';
import CodeEditor from './CodeEditor';
import FeedbackModal from './FeedbackModal';
import { getTemplateQuestions } from '../utils/interviewTemplates';
import { getUserPlan, hasProFeatures } from '../utils/plans';

// Use environment variables or fallback based on environment
const isDevelopment =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';
const WS_URL =
  import.meta.env.VITE_WS_URL ||
  (isDevelopment
    ? 'ws://localhost:5000'
    : 'wss://6kp1lphd5a.execute-api.eu-west-2.amazonaws.com/prod');
const API_URL =
  import.meta.env.VITE_API_URL ||
  (isDevelopment
    ? 'http://localhost:5000'
    : 'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com');
// Recordings (S3) use the main deployed API even when running locally
const RECORDINGS_API_URL =
  import.meta.env.VITE_RAG_API_URL ||
  import.meta.env.VITE_API_URL ||
  'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

interface InterviewStage {
  id: number;
  type: string;
  name: string;
  icon: string;
  duration: number;
  difficulty: string;
  order?: number;
}

interface InterviewConfig {
  type?: string;
  difficulty?: string;
  templateId?: string;
  templateQuestions?: string[]; // Template-specific questions for the interview type
  isMultiSession?: boolean;
  interviews?: InterviewStage[];
  totalDuration?: number;
  sessionId?: string;
  jobDescription?: string; // For focus_interview type
  caseStudyText?: string; // For case_study type
}

interface Message {
  sender: 'user' | 'ai' | 'system' | 'error';
  text: string;
  timestamp: Date | string;
  isStreaming?: boolean;
}

interface User {
  username?: string;
  email?: string;
}

interface Feedback {
  score?: number | null;
  summary?: string;
  improvements?: string[];
  strengths?: string[];
  speaking_clarity_analysis?: string;
  problem_solving_approach_score?: number | null;
  overall_score?: number;
  overall_assessment?: string;
  isSessionSummary?: boolean;
  totalScore?: number | null;
  stages?: Array<{
    type?: string;
    name?: string;
    score?: number | null;
    summary?: string;
  }>;
  stageName?: string; // For individual stage feedback in multi-session
  isStageFeedback?: boolean; // Flag to indicate this is stage feedback (for modal close handler)
  stageIndex?: number; // Current stage index (for validation)
}

interface StageFeedback {
  type?: string;
  name?: string;
  feedback: Feedback | null;
  score?: number | null;
  summary?: string | null;
  improvements?: string[]; // Bad points
  strengths?: string[]; // Good points
}

interface ChunkInfo {
  chunks: (string | null)[];
  total: number;
  type: string;
  received: number;
}

interface IncomingChunks {
  [messageId: string]: ChunkInfo;
}

interface InterviewSessionProps {
  config: InterviewConfig;
  onEnd: () => void;
  user: User | null;
  signOut: () => void;
}

interface WebSocketMessage {
  type: string;
  text?: string;
  message?: string;
  audio_data?: string;
  message_type?: string;
  message_id?: string;
  chunk_index?: number;
  total_chunks?: number;
  chunk_data?: string;
  error?: string;
  feedback?: string | Feedback;
  is_stage_feedback?: boolean;
}

interface VoiceResponse {
  transcript?: string;
  response?: string;
  audio_data?: string;
  error?: string;
}

interface CodeData {
  currentCode: string;
  codeHistory: string;
  output: string;
  timestamp: string;
}

const InterviewSession: React.FC<InterviewSessionProps> = ({
  config,
  onEnd,
  user,
  signOut,
}) => {
  const { getToken, user: authUser } = useAuth();
  const isPro = hasProFeatures(getUserPlan(authUser));
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isRecordingInterview, setIsRecordingInterview] =
    useState<boolean>(false);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false); // Camera preview without recording
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [isSubmittingRecording, setIsSubmittingRecording] =
    useState<boolean>(false);
  const [isSharingScreen, setIsSharingScreen] = useState<boolean>(false);
  const [isAIResponding, setIsAIResponding] = useState<boolean>(false);
  const isInitializingRef = useRef<boolean>(false); // Prevent duplicate initialization in React StrictMode
  const connectionMessageSentRef = useRef<boolean>(false); // Prevent duplicate "Connected" messages
  const [hasArchitectureImage, setHasArchitectureImage] =
    useState<boolean>(false);
  const [architectureImageData, setArchitectureImageData] = useState<
    string | null
  >(null);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState<boolean>(false);

  // Multi-session state
  const [isMultiSession, setIsMultiSession] = useState<boolean>(false);
  const [currentStageIndex, setCurrentStageIndex] = useState<number>(0);
  const [stageTimeRemaining, setStageTimeRemaining] = useState<number>(0);
  const [stageFeedbacks, setStageFeedbacks] = useState<StageFeedback[]>([]);
  const stageFeedbacksRef = useRef<StageFeedback[]>([]); // Ref to track stageFeedbacks for use in callbacks
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [sessionComplete, setSessionComplete] = useState<boolean>(false);
  const [awaitingUserFinish, setAwaitingUserFinish] = useState<boolean>(false);

  const audioQueueRef = useRef<string[]>([]);
  const isPlayingAudioRef = useRef<boolean>(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const incomingChunksRef = useRef<IncomingChunks>({});
  const screenShareRef = useRef<ScreenShareHandle>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const stageMessagesRef = useRef<Message[]>([]);
  const interviewRecorderRef = useRef<MediaRecorder | null>(null);
  const interviewStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  // Audio mixing for recording (to capture both mic + AI TTS)
  const audioContextRef = useRef<AudioContext | null>(null);
  const mixedDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(
    null
  );
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const currentTtsSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const requestStageFeedbackRef = useRef<(() => void) | null>(null);
  const proceedToNextStageRef = useRef<
    ((feedbackData: Feedback | null) => void) | null
  >(null);
  const completeSessionRef = useRef<(() => Promise<void>) | null>(null);
  const addMessageRef = useRef<
    ((sender: string, text: string, isChunk?: boolean) => void) | null
  >(null);
  const handleStageTimeUpRef = useRef<(() => void) | null>(null);
  const pendingStageFeedbackRef = useRef<Feedback | null>(null); // Store feedback temporarily while modal is open
  const isEndingSessionRef = useRef<boolean>(false); // Track if user clicked "End Interview" (so we complete instead of proceed)

  // Track window width for responsive screen share visibility
  useEffect(() => {
    const handleResize = (): void => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize multi-session if config indicates it
  useEffect(() => {
    if (
      config?.isMultiSession &&
      config?.interviews &&
      config.interviews.length > 0
    ) {
      setIsMultiSession(true);
      setCurrentStageIndex(0);
      setStageTimeRemaining(config.interviews[0].duration * 60);
      setStageFeedbacks([]);
      stageFeedbacksRef.current = []; // Clear ref
      stageFeedbacksRef.current = []; // Clear ref
      stageMessagesRef.current = [];
    }
  }, [config]);

  // Timer countdown for multi-session
  useEffect(() => {
    if (!isMultiSession || sessionComplete || isTransitioning) {
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      setStageTimeRemaining((prev) => {
        if (prev <= 1) {
          if (handleStageTimeUpRef.current) handleStageTimeUpRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isMultiSession, currentStageIndex, sessionComplete, isTransitioning]);

  // Get current stage config for multi-session
  const getCurrentStage = useCallback((): InterviewStage | null => {
    if (!isMultiSession || !config?.interviews) return null;
    return config.interviews[currentStageIndex] || null;
  }, [isMultiSession, config, currentStageIndex]);

  // Get the current interview type
  const getCurrentInterviewType = useCallback((): string => {
    if (isMultiSession) {
      const stage = getCurrentStage();
      return stage?.type || 'technical';
    }
    return config?.type || 'technical';
  }, [isMultiSession, getCurrentStage, config]);

  // Get current difficulty (kept for future use)
  // @ts-expect-error - Function kept for future use, intentionally unused
  const _getCurrentDifficulty = useCallback((): string => {
    if (isMultiSession) {
      const stage = getCurrentStage();
      return stage?.difficulty || 'medium';
    }
    return config?.difficulty || 'medium';
  }, [isMultiSession, getCurrentStage, config]);

  // Handle when stage time is up
  const handleStageTimeUp = useCallback((): void => {
    if (isRecording) {
      setAwaitingUserFinish(true);
      return;
    }
    if (requestStageFeedbackRef.current) {
      requestStageFeedbackRef.current();
    }
  }, [isRecording]);

  handleStageTimeUpRef.current = handleStageTimeUp;

  // Request feedback for current stage
  const requestStageFeedback = useCallback((): void => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected for stage feedback');
      if (proceedToNextStageRef.current) proceedToNextStageRef.current(null);
      return;
    }

    setIsTransitioning(true);
    setIsLoadingFeedback(true);

    const conversationData = stageMessagesRef.current.map((msg) => ({
      sender: msg.sender,
      text: msg.text,
      timestamp: msg.timestamp,
    }));

    const currentStage = getCurrentStage();

    try {
      wsRef.current.send(
        JSON.stringify({
          type: 'request_feedback',
          connection_id: connectionId,
          conversation: conversationData,
          interview_type: currentStage?.type || config?.type,
          is_stage_feedback: true,
        })
      );
      console.log('📊 Stage feedback request sent');
    } catch (error) {
      console.error('Error sending stage feedback request:', error);
      if (proceedToNextStageRef.current) proceedToNextStageRef.current(null);
    }
  }, [connectionId, config, getCurrentStage]);

  requestStageFeedbackRef.current = requestStageFeedback;

  // Proceed to next stage after feedback
  const proceedToNextStage = useCallback(
    (feedbackData: Feedback | null): void => {
      const currentStage = getCurrentStage();

      // Check if feedback for this stage is already stored (might have been stored when feedback was received)
      // Only add if not already present to avoid duplicates
      setStageFeedbacks((prev) => {
        const currentStageType = currentStage?.type;
        const currentStageName = currentStage?.name;

        // Check if feedback for this stage already exists
        const alreadyExists = prev.some(
          (sf) => sf.type === currentStageType && sf.name === currentStageName
        );

        if (alreadyExists) {
          console.log(
            '✅ Feedback already stored for this stage, skipping duplicate',
            {
              stageType: currentStageType,
              stageName: currentStageName,
              existingCount: prev.length,
            }
          );
          stageFeedbacksRef.current = prev; // Keep ref in sync
          return prev;
        }

        // Add feedback if not already present (shouldn't happen normally, but safety check)
        console.log(
          "➕ Adding feedback to stageFeedbacks (shouldn't happen - feedback should already be stored)",
          {
            stageType: currentStageType,
            stageName: currentStageName,
          }
        );
        const updated = [
          ...prev,
          {
            type: currentStageType,
            name: currentStageName,
            feedback: feedbackData,
            score: feedbackData?.score || null,
            summary:
              feedbackData?.summary || feedbackData?.overall_assessment || null,
          },
        ];
        stageFeedbacksRef.current = updated; // Keep ref in sync
        return updated;
      });

      stageMessagesRef.current = [];

      if (currentStageIndex < (config?.interviews?.length || 0) - 1) {
        const nextIndex = currentStageIndex + 1;
        const nextStage = config.interviews![nextIndex];

        setCurrentStageIndex(nextIndex);
        setStageTimeRemaining(nextStage.duration * 60);
        setIsTransitioning(false);
        setIsLoadingFeedback(false);

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          if (addMessageRef.current)
            addMessageRef.current(
              'system',
              `📋 Moving to ${nextStage.name} interview...`
            );

          const userName = user?.username || user?.email || null;

          // Get template questions for stage transition if template is selected
          let templateQuestions: string[] | undefined = undefined;
          if (config?.templateId && nextStage.type) {
            templateQuestions = getTemplateQuestions(
              config.templateId,
              nextStage.type
            );
            console.log(
              `📋 Stage transition: Template ${config.templateId} selected, found ${templateQuestions.length} questions for ${nextStage.type}`
            );
          }

          wsRef.current.send(
            JSON.stringify({
              type: 'start_interview',
              interview_type: nextStage.type,
              difficulty: nextStage.difficulty,
              user_name: userName,
              template_id: config?.templateId || undefined,
              template_questions: templateQuestions,
              is_stage_transition: true,
            })
          );
        }
      } else {
        // Last stage completed - don't auto-complete, just stop the timer
        // User will click "End Interview" to see session summary
        console.log(
          '✅ Last stage completed. Waiting for user to click "End Interview" to see session summary.'
        );
        setIsTransitioning(false);
        setIsLoadingFeedback(false);
        setStageTimeRemaining(0); // Stop timer
        // Don't call completeSession() here - wait for "End Interview" button
      }
    },
    [config, currentStageIndex, getCurrentStage, user]
  );

  proceedToNextStageRef.current = proceedToNextStage;

  // Complete the entire session
  const completeSession = useCallback(async (): Promise<void> => {
    // Use ref to get the latest stageFeedbacks to avoid stale closure issues
    const currentFeedbacks =
      stageFeedbacksRef.current.length > 0
        ? stageFeedbacksRef.current
        : stageFeedbacks;

    console.log('🏁 completeSession called', {
      stageFeedbacksCount: currentFeedbacks.length,
      stageFeedbacksFromState: stageFeedbacks.length,
      stageFeedbacksFromRef: stageFeedbacksRef.current.length,
      stageFeedbacks: currentFeedbacks.map((f) => ({
        type: f.type,
        name: f.name,
        score: f.score,
        hasSummary: !!f.summary,
      })),
    });

    setSessionComplete(true);
    setIsTransitioning(false);
    setIsLoadingFeedback(false);

    if (addMessageRef.current) {
      addMessageRef.current(
        'ai',
        'Thank you for completing all interview stages! I hope this practice session was helpful. Review your feedback below and keep practicing. Good luck with your real interviews! 🎉'
      );
    }

    const username = user?.username || user?.email?.split('@')[0] || 'unknown';

    // Save simplified feedback structure (only score and summary, no nested feedback object)
    // Ensure scores are numbers, not null if feedback was received
    // Map feedbacks to match the interviews array order to ensure correct mapping
    const simplifiedFeedbacks = (config?.interviews || []).map(
      (interview, index) => {
        // Find corresponding feedback for this interview (by index or by type/name match)
        const stageFeedback = currentFeedbacks.find(
          (sf) =>
            (sf.type === interview.type && sf.name === interview.name) ||
            currentFeedbacks.indexOf(sf) === index
        );

        if (!stageFeedback) {
          console.warn(
            `⚠️ No feedback found for interview ${index}: ${
              interview.name || interview.type
            }`
          );
          return {
            score: null,
            summary: null,
          };
        }

        // Extract score - prefer score field, fallback to feedback.score or feedback.overall_score
        let finalScore: number | null = null;
        if (stageFeedback.score !== null && stageFeedback.score !== undefined) {
          finalScore =
            typeof stageFeedback.score === 'number'
              ? stageFeedback.score
              : null;
        } else if (
          stageFeedback.feedback?.score !== null &&
          stageFeedback.feedback?.score !== undefined
        ) {
          finalScore =
            typeof stageFeedback.feedback.score === 'number'
              ? stageFeedback.feedback.score
              : null;
        } else if (
          stageFeedback.feedback?.overall_score !== null &&
          stageFeedback.feedback?.overall_score !== undefined
        ) {
          finalScore =
            typeof stageFeedback.feedback.overall_score === 'number'
              ? stageFeedback.feedback.overall_score
              : null;
        }

        // Extract summary - prefer summary field, fallback to feedback.summary or feedback.overall_assessment
        let finalSummary: string | null = null;
        if (stageFeedback.summary) {
          finalSummary = stageFeedback.summary;
        } else if (stageFeedback.feedback?.summary) {
          finalSummary = stageFeedback.feedback.summary;
        } else if (stageFeedback.feedback?.overall_assessment) {
          finalSummary = stageFeedback.feedback.overall_assessment;
        }

        // Extract improvements (bad points) - prefer direct field, fallback to feedback object
        let finalImprovements: string[] | null = null;
        if (
          stageFeedback.improvements &&
          Array.isArray(stageFeedback.improvements) &&
          stageFeedback.improvements.length > 0
        ) {
          finalImprovements = stageFeedback.improvements;
        } else if (
          stageFeedback.feedback?.improvements &&
          Array.isArray(stageFeedback.feedback.improvements) &&
          stageFeedback.feedback.improvements.length > 0
        ) {
          finalImprovements = stageFeedback.feedback.improvements;
        }

        // Extract strengths (good points) - prefer direct field, fallback to feedback object
        let finalStrengths: string[] | null = null;
        if (
          stageFeedback.strengths &&
          Array.isArray(stageFeedback.strengths) &&
          stageFeedback.strengths.length > 0
        ) {
          finalStrengths = stageFeedback.strengths;
        } else if (
          stageFeedback.feedback?.strengths &&
          Array.isArray(stageFeedback.feedback.strengths) &&
          stageFeedback.feedback.strengths.length > 0
        ) {
          finalStrengths = stageFeedback.feedback.strengths;
        }

        console.log(
          `📊 Extracted feedback for interview ${index} (${
            interview.name || interview.type
          }):`,
          {
            score: finalScore,
            hasSummary: !!finalSummary,
            summaryLength: finalSummary?.length || 0,
            improvementsCount: finalImprovements?.length || 0,
            strengthsCount: finalStrengths?.length || 0,
            stageFeedbackKeys: Object.keys(stageFeedback),
            feedbackKeys: stageFeedback.feedback
              ? Object.keys(stageFeedback.feedback)
              : [],
          }
        );

        return {
          score: finalScore,
          summary: finalSummary,
          improvements: finalImprovements,
          strengths: finalStrengths,
        };
      }
    );

    console.log('💾 Preparing to save session with feedbacks:', {
      interviewCount: config?.interviews?.length || 0,
      feedbackCount: currentFeedbacks.length,
      simplifiedFeedbackCount: simplifiedFeedbacks.length,
      stageFeedbacks: currentFeedbacks.map((f) => ({
        type: f.type,
        name: f.name,
        score: f.score,
        hasSummary: !!f.summary,
      })),
      simplifiedFeedbacks: simplifiedFeedbacks.map((f, i) => ({
        index: i,
        score: f.score,
        hasSummary: !!f.summary,
        summaryPreview: f.summary?.substring(0, 50) || 'null',
        improvementsCount: f.improvements?.length || 0,
        strengthsCount: f.strengths?.length || 0,
      })),
      sessionDataPreview: {
        sessionId: config?.sessionId || `session_${Date.now()}`,
        interviewCount: config?.interviews?.length || 0,
        feedbackCount: simplifiedFeedbacks.length,
        username,
      },
    });

    // Collect all messages for transcript export (both from current session and stage messages)
    const allMessages = [...messages, ...(stageMessagesRef.current || [])].map(
      (msg) => ({
        sender: msg.sender,
        text: msg.text,
        timestamp:
          typeof msg.timestamp === 'string'
            ? msg.timestamp
            : msg.timestamp.toISOString(),
      })
    );

    const sessionData = {
      sessionId: config?.sessionId || `session_${Date.now()}`,
      date: new Date().toISOString(),
      interviews: config?.interviews || [],
      feedbacks: simplifiedFeedbacks, // Save simplified structure - matches interviews array order
      // Each feedback should have: score, summary, improvements, strengths
      totalDuration: config?.totalDuration || 0,
      username,
      messages: allMessages, // Include conversation messages for transcript export
    };

    console.log('💾 Final sessionData being saved:', {
      sessionId: sessionData.sessionId,
      feedbackCount: sessionData.feedbacks.length,
      feedbacksPreview: sessionData.feedbacks.map((f, i) => ({
        index: i,
        hasScore: f.score !== null && f.score !== undefined,
        score: f.score,
        hasSummary: !!f.summary,
        hasImprovements: Array.isArray(f.improvements),
        improvementsCount: f.improvements?.length || 0,
        hasStrengths: Array.isArray(f.strengths),
        strengthsCount: f.strengths?.length || 0,
        keys: Object.keys(f),
      })),
    });

    try {
      const storageKey = `interview_sessions_${username}`;
      const existingSessions = JSON.parse(
        localStorage.getItem(storageKey) || '[]'
      );
      existingSessions.unshift(sessionData);
      const trimmedSessions = existingSessions.slice(0, 50);
      localStorage.setItem(storageKey, JSON.stringify(trimmedSessions));
      console.log('Session saved to localStorage');
    } catch (error) {
      console.error('Error saving session to localStorage:', error);
    }

    try {
      console.log('💾 Attempting to save session to backend...', {
        sessionId: sessionData.sessionId,
        username: sessionData.username,
        interviewCount: sessionData.interviews.length,
        feedbackCount: sessionData.feedbacks.length,
      });

      const response = await fetch(`${API_URL}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${getToken()}`,
        },
        body: JSON.stringify(sessionData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Session saved to backend successfully:', result);
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('❌ Failed to save session to backend:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
      }
    } catch (error) {
      console.error('❌ Error saving session to backend:', error);
    }

    // Don't show modal here - this function just prepares and saves the session
    // The modal will be shown from handleEndInterview when user clicks "End Interview"
    console.log(
      '✅ Session data prepared and saved. Session summary modal will be shown when user clicks "End Interview".'
    );
  }, [config, stageFeedbacks, messages, user, getToken]);

  completeSessionRef.current = completeSession;

  // Handle user finish speaking when awaiting
  useEffect(() => {
    if (awaitingUserFinish && !isRecording) {
      setAwaitingUserFinish(false);
      if (requestStageFeedbackRef.current) requestStageFeedbackRef.current();
    }
  }, [awaitingUserFinish, isRecording]);

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    console.log('🔍 Config useEffect triggered:', {
      hasConfig: !!config,
      configType: config?.type,
      isMultiSession: config?.isMultiSession,
      hasInterviews: !!(config?.interviews && config.interviews.length > 0),
      interviewsLength: config?.interviews?.length || 0,
    });

    // Initialize session for both single interview (has type) and multi-session (has isMultiSession and interviews)
    const shouldInitialize =
      config &&
      (config.type ||
        (config.isMultiSession &&
          config.interviews &&
          config.interviews.length > 0));

    console.log('🔍 Should initialize session?', shouldInitialize);

    if (shouldInitialize) {
      // IMPORTANT: Reset connectionId and clear state when starting a new interview
      // This prevents reusing old connection state from previous interviews
      console.log(
        '🧹 Resetting connectionId and clearing state for new interview session'
      );
      setConnectionId(null);
      setMessages([]);
      setIsConnected(false);
      setIsRecording(false);
      setIsAIResponding(false);
      stageMessagesRef.current = [];
      setStageFeedbacks([]);
      stageFeedbacksRef.current = []; // Clear ref
      pendingStageFeedbackRef.current = null;
      connectionMessageSentRef.current = false; // Reset connection message flag

      // Prevent duplicate initialization in React StrictMode (development)
      // Check ref FIRST (before checking state) since ref persists across StrictMode double-invocation
      if (isInitializingRef.current) {
        console.warn(
          '⚠️ Session initialization already in progress, skipping duplicate call'
        );
        return;
      }

      // Check if WebSocket is already connected or connecting
      const currentWs = wsRef.current || ws;
      if (
        currentWs &&
        (currentWs.readyState === WebSocket.OPEN ||
          currentWs.readyState === WebSocket.CONNECTING)
      ) {
        console.warn(
          '⚠️ WebSocket already connected/connecting, skipping duplicate initialization'
        );
        return;
      }

      console.log('🚀 Calling initializeSession...');
      // Set ref IMMEDIATELY before any async operations
      isInitializingRef.current = true;
      initializeSession().finally(() => {
        // Reset after a delay to allow cleanup
        setTimeout(() => {
          isInitializingRef.current = false;
        }, 3000);
      });
    } else {
      console.warn('⚠️ NOT initializing session. Config:', config);
    }

    return () => {
      // Only reset refs if WebSocket is actually being closed (not just StrictMode cleanup)
      const currentWs = wsRef.current;
      if (currentWs) {
        try {
          if (
            currentWs.readyState === WebSocket.OPEN ||
            currentWs.readyState === WebSocket.CONNECTING
          ) {
            currentWs.close(1000, 'Session ended');
            // Reset refs only when actually closing the connection
            isInitializingRef.current = false;
            connectionMessageSentRef.current = false;
          }
        } catch (error) {
          console.error('Error closing WebSocket:', error);
        }
        wsRef.current = null;
      } else {
        // If no WebSocket exists, we're in cleanup before connection established
        // Don't reset refs here - let them persist to prevent StrictMode duplicates
      }
      setIsConnected(false);
      setWs(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const initializeSession = async (): Promise<void> => {
    console.log('🔧 initializeSession called with config:', {
      hasConfig: !!config,
      isMultiSession: config?.isMultiSession,
      hasType: !!config?.type,
      type: config?.type,
      hasInterviews: !!(config?.interviews && config.interviews.length > 0),
      interviewsLength: config?.interviews?.length || 0,
      interviews: config?.interviews?.map((i) => ({
        type: i.type,
        difficulty: i.difficulty,
        name: i.name,
      })),
    });

    if (!config) {
      console.error('❌ Cannot initialize session: missing config');
      return;
    }

    // For multi-session, get type from first interview; for single session, use config.type
    const interviewType =
      config.isMultiSession && config.interviews && config.interviews.length > 0
        ? config.interviews[0].type
        : config.type;

    console.log('🔧 Extracted interviewType:', interviewType);

    if (!interviewType) {
      console.error(
        '❌ Cannot initialize session: missing interview type. Full config:',
        JSON.stringify(config, null, 2)
      );
      return;
    }

    // Check both ws state and wsRef to ensure we don't have a stale connection
    const currentWs = wsRef.current || ws;
    if (currentWs) {
      if (currentWs.readyState === WebSocket.OPEN) {
        console.warn('⚠️ WebSocket already connected, skipping initialization');
        return;
      } else if (currentWs.readyState === WebSocket.CONNECTING) {
        console.warn(
          '⚠️ WebSocket is already connecting, skipping initialization'
        );
        return;
      } else {
        // Close any stale connections
        console.log('🧹 Cleaning up stale WebSocket connection...');
        try {
          currentWs.close();
        } catch (e) {
          console.warn('Error closing stale WebSocket:', e);
        }
        wsRef.current = null;
        setWs(null);
        setIsConnected(false);
      }
    }

    console.log('✅ Proceeding with WebSocket initialization...', {
      hasExistingWs: !!currentWs,
      wsState: currentWs?.readyState,
      isDevelopment,
      WS_URL,
      API_URL,
    });

    try {
      let connId: string | null = null;
      let wsUrl = '';

      if (isDevelopment) {
        const url = `${API_URL}/start-interview`;

        const userName = user?.username || user?.email || null;

        // For multi-session, use first interview's type and difficulty
        const difficulty =
          config.isMultiSession &&
          config.interviews &&
          config.interviews.length > 0
            ? config.interviews[0].difficulty
            : config.difficulty;

        console.log('Starting interview session, API URL:', url);
        console.log('Request payload:', {
          interview_type: interviewType,
          difficulty: difficulty || 'medium',
          isMultiSession: config.isMultiSession || false,
        });

        // Get template questions if template is selected
        let templateQuestions: string[] | undefined = undefined;
        if (config?.templateId && interviewType) {
          templateQuestions = getTemplateQuestions(config.templateId, interviewType);
          console.log(
            `📋 HTTP: Template ${config.templateId} selected, found ${templateQuestions.length} questions for ${interviewType}`
          );
        }

        const requestBody: Record<string, unknown> = {
          interview_type: interviewType,
          difficulty: difficulty || 'medium',
          user_name: userName,
          template_id: config?.templateId || undefined,
          template_questions: templateQuestions,
        };

        // Include job description for focus_interview type
        if (interviewType === 'focus_interview' && config.jobDescription) {
          requestBody.job_description = config.jobDescription;
        }

        // Include case study text for case_study type
        if (interviewType === 'case_study' && config.caseStudyText) {
          requestBody.case_study_text = config.caseStudyText;
        }

        const token = getToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers['Authorization'] = `Basic ${token}`;
        }

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
        }).catch((error) => {
          console.error('Fetch error details:', error);
          throw new Error(
            `Network error: ${error.message}. Check if backend is running at ${API_URL}`
          );
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error('Response error:', response.status, errorText);
          throw new Error(
            `Failed to start interview: ${response.status} ${response.statusText}. ${errorText}`
          );
        }

        const data = await response.json();
        connId = data.connection_id;

        if (!connId) {
          throw new Error('No connection ID received from server');
        }

        wsUrl = `${WS_URL}/ws/${connId}`;
      } else {
        const token = getToken();
        console.log(
          '🔐 Auth token available:',
          !!token,
          token
            ? `length=${token.length}, preview=${token.substring(0, 30)}...`
            : '(none)'
        );

        if (!token) {
          console.error('❌ No auth token available - user must be logged in');
          if (addMessageRef.current) {
            addMessageRef.current(
              'error',
              'Authentication required. Please sign out and sign in again to refresh your session.'
            );
          }
          return;
        }

        wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
        console.log(`🔗 Connecting to WebSocket: ${WS_URL} (with token)`);
        console.log(`🔗 Token length: ${token.length} chars`);
        connId = 'aws-managed';
      }

      setConnectionId(connId);

      console.log('Connecting to WebSocket:', wsUrl);
      const websocket = new WebSocket(wsUrl);

      websocket.onopen = (): void => {
        setIsConnected(true);
        setWs(websocket);
        wsRef.current = websocket;
        // Prevent duplicate "Connected" messages in React StrictMode
        if (!connectionMessageSentRef.current && addMessageRef.current) {
          connectionMessageSentRef.current = true;
          addMessageRef.current('system', 'Connected to AI Interviewer');
        }

        // Start WebSocket keepalive ping to prevent 10-minute timeout
        // AWS API Gateway WebSocket has 10-minute idle timeout, so ping every 5 minutes
        const keepaliveInterval = setInterval(() => {
          const currentWs = wsRef.current || ws;
          if (currentWs && currentWs.readyState === WebSocket.OPEN) {
            try {
              // Send ping message to keep connection alive
              currentWs.send(JSON.stringify({ type: 'ping' }));
              console.log('💓 WebSocket keepalive ping sent');
            } catch (error) {
              console.error('❌ Error sending keepalive ping:', error);
              clearInterval(keepaliveInterval);
            }
          } else {
            console.warn('⚠️ WebSocket not open, stopping keepalive');
            clearInterval(keepaliveInterval);
          }
        }, 5 * 60 * 1000); // 5 minutes (half of 10-minute timeout)

        // Store interval ID for cleanup
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (websocket as any).__keepaliveInterval = keepaliveInterval;

        if (!isDevelopment) {
          console.log('Sending start_interview message to AWS WebSocket');
          const userName = user?.username || user?.email || null;
          // For multi-session, use first interview's type and difficulty
          const wsInterviewType =
            config.isMultiSession &&
            config.interviews &&
            config.interviews.length > 0
              ? config.interviews[0].type
              : interviewType;
          const wsDifficulty =
            config.isMultiSession &&
            config.interviews &&
            config.interviews.length > 0
              ? config.interviews[0].difficulty
              : config.difficulty || 'medium';

          // Get template questions if template is selected
          let templateQuestions: string[] | undefined = undefined;
          if (config?.templateId && wsInterviewType) {
            templateQuestions = getTemplateQuestions(
              config.templateId,
              wsInterviewType
            );
            console.log(
              `📋 Template ${config.templateId} selected, found ${templateQuestions.length} questions for ${wsInterviewType}`
            );
          }

          websocket.send(
            JSON.stringify({
              type: 'start_interview',
              interview_type: wsInterviewType,
              difficulty: wsDifficulty,
              user_name: userName,
              template_id: config?.templateId || undefined,
              template_questions: templateQuestions,
            })
          );
        }
      };

      websocket.onmessage = (event: MessageEvent): void => {
        try {
          const data = JSON.parse(event.data) as WebSocketMessage;
          console.log('📨 WebSocket message received:', data.type, {
            type: data.type,
            text: data.text?.substring(0, 100),
            message: data.message?.substring?.(0, 100),
            hasAudioData: !!data.audio_data,
            audioDataLength: data.audio_data?.length || 0,
            messageType: data.message_type,
            chunkIndex: data.chunk_index,
            totalChunks: data.total_chunks,
            error: data.error,
          });
          handleWebSocketMessage(data);
        } catch (error) {
          console.error(
            '❌ Error parsing WebSocket message:',
            error,
            event.data?.toString().substring(0, 100)
          );
        }
      };

      websocket.onerror = (error: Event): void => {
        console.error('❌ WebSocket error:', error);
        console.error('❌ WebSocket details:', {
          readyState: websocket.readyState,
          url: websocket.url,
        });
        if (addMessageRef.current) {
          addMessageRef.current(
            'error',
            'WebSocket connection error. Please check your connection and try again.'
          );
        }
        setIsConnected(false);
      };

      websocket.onclose = (event: CloseEvent): void => {
        // Clear keepalive interval
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const keepaliveInterval = (websocket as any).__keepaliveInterval;
        if (keepaliveInterval) {
          clearInterval(keepaliveInterval);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          delete (websocket as any).__keepaliveInterval;
        }

        setIsConnected(false);
        setWs(null);
        wsRef.current = null;

        const codeNames: Record<number, string> = {
          1000: 'Normal Closure',
          1001: 'Going Away',
          1002: 'Protocol Error',
          1003: 'Unsupported Data',
          1006: 'Abnormal Closure (No Close Frame)',
          1007: 'Invalid Frame Payload Data',
          1008: 'Policy Violation',
          1009: 'Message Too Big',
          1010: 'Mandatory Extension',
          1011: 'Internal Server Error',
          4001: 'Authentication Failed',
        };

        console.error('❌ WebSocket closed:', {
          code: event.code,
          codeName: codeNames[event.code] || 'Unknown',
          reason: event.reason,
          wasClean: event.wasClean,
        });

        if (event.code !== 1000) {
          let disconnectMessage = 'Disconnected from server';

          if (event.code === 1006) {
            disconnectMessage =
              'Connection failed immediately. Possible causes:\n' +
              '1. Authentication token is invalid or expired\n' +
              '2. WebSocket API is not deployed or unavailable\n' +
              '3. Network connectivity issue\n\n' +
              'Please try:\n' +
              '1. Sign out and sign in again to refresh your token\n' +
              '2. Check your internet connection\n' +
              '3. Verify the backend is deployed and accessible';
          } else if (event.code === 1001) {
            disconnectMessage = 'Server is going away.';
          } else if (
            event.code === 4001 ||
            event.reason?.includes('401') ||
            event.reason?.includes('Unauthorized')
          ) {
            disconnectMessage =
              'Authentication failed (401). The server rejected your authentication token.\n' +
              'Please sign out and sign in again to get a fresh token.';
          } else if (event.code === 1008) {
            disconnectMessage =
              'Connection rejected by server (Policy Violation).\n' +
              'This may be a CORS or authentication configuration issue.';
          } else if (event.code === 1011) {
            disconnectMessage =
              'Server error occurred (500). The backend may have crashed.\n' +
              'Please try again later or check CloudWatch logs.';
          } else if (event.reason) {
            disconnectMessage = `Disconnected: ${event.reason}`;
          }

          console.error(
            `❌ Connection failed with code ${event.code} (${
              codeNames[event.code] || 'Unknown'
            }): ${disconnectMessage}`
          );
          if (addMessageRef.current) {
            addMessageRef.current('error', disconnectMessage);
          }
        }
      };

      setWs(websocket);
      wsRef.current = websocket;
    } catch (error) {
      console.error('Error initializing session:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (addMessageRef.current) {
        addMessageRef.current(
          'error',
          `Failed to start interview session: ${errorMessage}`
        );
      }
    }
  };

  const reassembleIncomingChunks = (
    messageId: string,
    chunkIndex: number,
    totalChunks: number,
    chunkData: string,
    messageType: string
  ): {
    complete: boolean;
    data?: string;
    type?: string;
    chunkInfo?: ChunkInfo;
  } => {
    if (!incomingChunksRef.current[messageId]) {
      incomingChunksRef.current[messageId] = {
        chunks: Array(totalChunks).fill(null),
        total: totalChunks,
        type: messageType,
        received: 0,
      };
    }

    const chunkInfo = incomingChunksRef.current[messageId];
    chunkInfo.chunks[chunkIndex] = chunkData;
    chunkInfo.received += 1;

    console.log(
      `📦 Received chunk ${
        chunkIndex + 1
      }/${totalChunks} for ${messageType} message ${messageId}`
    );

    if (chunkInfo.received === totalChunks) {
      const completeData = chunkInfo.chunks.join('');
      const completeType = chunkInfo.type;
      delete incomingChunksRef.current[messageId];
      console.log(
        `✅ All chunks received for ${completeType} message ${messageId}, reassembled ${completeData.length} chars`
      );
      return {
        complete: true,
        data: completeData,
        type: completeType,
      };
    }

    return { complete: false, chunkInfo };
  };

  const handleWebSocketMessage = (data: WebSocketMessage): void => {
    if (
      data.type === 'chunk' &&
      data.message_type &&
      data.message_id !== undefined
    ) {
      const reassembled = reassembleIncomingChunks(
        data.message_id,
        data.chunk_index!,
        data.total_chunks!,
        data.chunk_data!,
        data.message_type
      );

      if (
        reassembled.complete &&
        reassembled.data &&
        reassembled.type === 'voice_response'
      ) {
        console.log('🔊 Received complete chunked voice response', {
          dataLength: reassembled.data.length,
          preview: reassembled.data.substring(0, 50) + '...',
        });
        setIsAIResponding(false);
        window.speechSynthesis.cancel();
        if (reassembled.data.length > 0) {
          console.log('🔊 Adding reassembled voice to queue');
          audioQueueRef.current.push(reassembled.data);
          console.log(
            '🔊 Queue length after adding:',
            audioQueueRef.current.length
          );
          processAudioQueue();
        } else {
          console.warn('⚠️ Reassembled voice data is empty');
        }
      } else if (reassembled.chunkInfo) {
        console.log(
          `📦 Still waiting for chunks (${reassembled.chunkInfo.received}/${reassembled.chunkInfo.total})`
        );
      }
      return;
    }

    switch (data.type) {
      case 'welcome':
        console.log('Welcome message received:', data.message);
        if (data.message && addMessageRef.current) {
          addMessageRef.current('system', data.message);
        }
        break;
      case 'transcript':
        if (data.text && data.text.trim()) {
          console.log('📝 Received transcript from backend:', data.text);
          if (addMessageRef.current) {
            addMessageRef.current('user', data.text);
          }
        } else {
          console.warn('⚠️ Received empty transcript from backend');
        }
        break;
      case 'request_screen_frame':
        console.log('📸 Backend requested screen frame - capturing now');
        if (isSharingScreen && screenShareRef.current) {
          screenShareRef.current.captureFrame();
        } else {
          console.warn('⚠️ Cannot capture frame - screen share not active');
        }
        break;
      case 'user_message':
        if (data.text && data.text.trim() && addMessageRef.current) {
          addMessageRef.current('user', data.text);
        }
        break;
      case 'response_chunk':
        setIsAIResponding(true);
        if (data.text && addMessageRef.current) {
          addMessageRef.current('ai', data.text, true);
        }
        break;
      case 'response_complete':
        setMessages((prev) => {
          if (prev.length > 0 && prev[prev.length - 1].isStreaming) {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              isStreaming: false,
            };
            return updated;
          }
          return prev;
        });
        setIsAIResponding(false);
        break;
      case 'interrupted':
        setIsAIResponding(false);
        if (addMessageRef.current) {
          addMessageRef.current('system', 'Response interrupted');
        }
        break;
      case 'screen_analysis_chunk':
        if (data.text && addMessageRef.current) {
          addMessageRef.current('ai', data.text, true);
        }
        break;
      case 'screen_analysis_complete':
        setMessages((prev) => {
          if (prev.length > 0 && prev[prev.length - 1].isStreaming) {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              isStreaming: false,
            };
            return updated;
          }
          return prev;
        });
        setIsAIResponding(false);
        break;
      case 'voice_response':
        console.log('🔊 voice_response message received:', {
          hasAudioData: !!data.audio_data,
          audioDataLength: data.audio_data?.length || 0,
          keys: Object.keys(data),
        });
        setIsAIResponding(false);
        window.speechSynthesis.cancel();
        if (data.audio_data && data.audio_data.length > 0) {
          console.log('🔊 Received backend voice response (OpenAI TTS)', {
            audioLength: data.audio_data.length,
            preview: data.audio_data.substring(0, 50) + '...',
          });
          audioQueueRef.current.push(data.audio_data);
          console.log(
            '🔊 Audio queued, queue length:',
            audioQueueRef.current.length
          );
          processAudioQueue();
        } else {
          console.warn(
            '⚠️ voice_response received but no audio_data field or empty'
          );
        }
        break;
      case 'error':
      case 'plan_required':
        if (data.message && addMessageRef.current) {
          addMessageRef.current('error', data.message);
        }
        break;
      case 'feedback_response':
        setIsLoadingFeedback(false);
        try {
          const feedbackData =
            typeof data.feedback === 'string'
              ? (JSON.parse(data.feedback) as Feedback)
              : (data.feedback as Feedback);

          // Check if this is a stage feedback (either isTransitioning is true OR data has is_stage_feedback flag)
          const isStageFeedbackRequest =
            isTransitioning || data.is_stage_feedback === true;

          console.log('📊 Feedback response received', {
            isMultiSession,
            isTransitioning,
            isStageFeedbackRequest,
            hasIsStageFeedback: data.is_stage_feedback !== undefined,
            currentStageIndex,
          });

          if (isMultiSession && isStageFeedbackRequest) {
            // For multi-session: Store feedback data temporarily and show modal for current stage
            const currentStage = getCurrentStage();
            const stageScore =
              feedbackData?.score || feedbackData?.overall_score;

            // Store feedback in stageFeedbacks BEFORE showing modal
            // This ensures it's saved even if user closes modal quickly
            // IMPORTANT: Store the full feedback object so we can extract all fields later
            const stageFeedbackToStore: StageFeedback = {
              type: currentStage?.type,
              name: currentStage?.name,
              feedback: feedbackData, // Store full feedback object
              score:
                stageScore !== undefined && stageScore !== null
                  ? stageScore
                  : null,
              summary:
                feedbackData?.summary ||
                feedbackData?.overall_assessment ||
                null,
              // Also store improvements and strengths directly for easier access
              improvements: feedbackData?.improvements || [],
              strengths: feedbackData?.strengths || [],
            };

            setStageFeedbacks((prev) => {
              const updated = [...prev, stageFeedbackToStore];
              stageFeedbacksRef.current = updated; // Keep ref in sync
              console.log('✅ Stage feedback stored in stageFeedbacks', {
                hasFeedback: !!feedbackData,
                stageName: currentStage?.name,
                score: stageScore,
                totalFeedbacks: updated.length,
              });
              return updated;
            });

            const stageFeedbackForModal = {
              score:
                stageScore !== undefined && stageScore !== null
                  ? stageScore
                  : null,
              summary:
                feedbackData?.summary ||
                feedbackData?.overall_assessment ||
                'Feedback generated successfully.',
              improvements: feedbackData?.improvements || [],
              strengths: feedbackData?.strengths || [],
              isSessionSummary: false,
              stageName: currentStage?.name || 'Current Stage', // Add stage name for clarity
            };

            // Store feedback data to use when modal closes - THIS IS KEY for proceeding to next stage
            // IMPORTANT: Store the feedback data with a flag indicating it's a stage transition feedback
            pendingStageFeedbackRef.current = {
              ...feedbackData,
              isStageFeedback: true, // Explicitly mark this as stage feedback
              stageIndex: currentStageIndex, // Store current stage index for validation
            } as Feedback;
            console.log('✅ Stage feedback stored in pendingStageFeedbackRef', {
              hasFeedback: !!feedbackData,
              stageName: currentStage?.name,
              stageIndex: currentStageIndex,
              isTransitioning,
            });

            // IMPORTANT: Keep isTransitioning true so modal close handler knows to proceed
            // Don't clear it until we actually proceed to next stage
            if (!isTransitioning) {
              setIsTransitioning(true);
              console.log(
                '⚠️ isTransitioning was false, setting it to true for stage feedback'
              );
            }

            // Normal stage transition - show modal, proceed to next stage on close (if more stages exist) or stay (if last)
            setFeedback(stageFeedbackForModal);
            setShowFeedbackModal(true);
          } else {
            // Single interview: Show feedback and save immediately with transcript
            pendingStageFeedbackRef.current = feedbackData;
            setFeedback(feedbackData);
            setShowFeedbackModal(true);

            // Save feedback immediately when received (with transcript)
            saveFeedbackToBackend(feedbackData, true);
          }
        } catch (error) {
          console.error('Error parsing feedback:', error);
          if (isMultiSession && isTransitioning) {
            if (proceedToNextStageRef.current)
              proceedToNextStageRef.current(null);
          } else {
            if (addMessageRef.current) {
              addMessageRef.current(
                'error',
                'Error receiving feedback. Please try again.'
              );
            }
          }
          setIsLoadingFeedback(false);
        }
        break;
      case 'feedback_error':
        setIsLoadingFeedback(false);
        if (addMessageRef.current) {
          addMessageRef.current(
            'error',
            data.message || 'Error generating feedback. Please try again.'
          );
        }
        break;
      case 'ping':
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'pong' }));
        }
        break;
      case 'pong':
        break;
      case 'architecture_image_uploaded':
        setHasArchitectureImage(true);
        break;
      default:
        console.log('Unknown message type:', data.type, data);
    }
  };

  const interruptCurrentAudio = (): void => {
    console.log('🔇 Interrupting current audio/TTS playback');

    // Stop normal Audio element playback
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    // Stop Web Audio API TTS playback (used during recording)
    if (currentTtsSourceRef.current) {
      try {
        currentTtsSourceRef.current.stop();
      } catch {
        // Ignore if already stopped
      }
      currentTtsSourceRef.current = null;
    }

    audioQueueRef.current = [];

    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    isPlayingAudioRef.current = false;
  };

  const addMessage = (sender: string, text: string, isChunk = false): void => {
    if (!text || (typeof text === 'string' && text.trim() === '')) {
      console.warn('Attempted to add empty message:', { sender, text });
      return;
    }

    const newMessage: Message = {
      sender: sender as Message['sender'],
      text: String(text),
      timestamp: new Date(),
      isStreaming: isChunk,
    };

    if (isMultiSession && (sender === 'user' || sender === 'ai')) {
      stageMessagesRef.current.push(newMessage);
    }

    setMessages((prev) => {
      if (
        isChunk &&
        prev.length > 0 &&
        prev[prev.length - 1].sender === 'ai' &&
        prev[prev.length - 1].isStreaming
      ) {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          text: updated[updated.length - 1].text + text,
        };
        return updated;
      } else {
        return [...prev, newMessage];
      }
    });
  };

  addMessageRef.current = addMessage;

  const processAudioQueue = (): void => {
    console.log('🔊 processAudioQueue called', {
      isPlaying: isPlayingAudioRef.current,
      queueLength: audioQueueRef.current.length,
    });

    if (isPlayingAudioRef.current) {
      console.log('🔊 Already playing audio, waiting...');
      return;
    }

    if (audioQueueRef.current.length === 0) {
      console.log('🔊 Audio queue is empty');
      return;
    }

    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    const audioBase64 = audioQueueRef.current.shift();
    if (audioBase64) {
      console.log(
        `🔊 Processing audio from queue: ${audioBase64.length} chars`
      );
      playAudio(audioBase64);
    }
  };

  // Play TTS audio through Web Audio API (used when recording to capture in recording)
  const playAudioThroughWebAudio = async (
    audioBase64: string
  ): Promise<void> => {
    const audioContext = audioContextRef.current;
    const mixedDestination = mixedDestinationRef.current;

    if (!audioContext || !mixedDestination) {
      console.warn(
        'AudioContext not available, falling back to normal playback'
      );
      playAudioNormal(audioBase64);
      return;
    }

    try {
      // Resume AudioContext if suspended (browser autoplay policy)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        console.log('🔊 AudioContext resumed');
      }

      // Decode base64 to ArrayBuffer
      const binaryString = atob(audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(bytes.buffer);
      console.log(
        '🔊 Audio decoded via Web Audio API, duration:',
        audioBuffer.duration
      );

      // Create source node
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // Stop any currently playing TTS
      if (currentTtsSourceRef.current) {
        try {
          currentTtsSourceRef.current.stop();
        } catch {
          // Ignore if already stopped
        }
      }
      currentTtsSourceRef.current = source;

      // Connect to both mixer (for recording) and speakers (for listening)
      source.connect(mixedDestination);
      source.connect(audioContext.destination);

      source.onended = () => {
        console.log('🔊 Web Audio TTS playback ended');
        isPlayingAudioRef.current = false;
        currentTtsSourceRef.current = null;
        setTimeout(() => {
          processAudioQueue();
        }, 500);
      };

      source.start(0);
      console.log(
        '✅ TTS playing through Web Audio API (captured in recording)'
      );
    } catch (err) {
      console.error('❌ Web Audio playback failed:', err);
      // Fallback to normal audio playback
      playAudioNormal(audioBase64);
    }
  };

  // Normal audio playback (when not recording)
  const playAudioNormal = (audioBase64: string): void => {
    const audio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
    currentAudioRef.current = audio;

    audio.volume = 1.0;
    audio.preload = 'auto';

    audio.onerror = (): void => {
      console.error('❌ Audio playback error:', {
        code: audio.error?.code,
        message: audio.error?.message,
      });
      isPlayingAudioRef.current = false;
      currentAudioRef.current = null;
      processAudioQueue();
    };

    audio.onloadeddata = (): void => {
      console.log('🔊 Audio loaded successfully, duration:', audio.duration);
    };

    audio.onended = (): void => {
      console.log('🔊 Audio playback ended');
      isPlayingAudioRef.current = false;
      currentAudioRef.current = null;
      setTimeout(() => {
        processAudioQueue();
      }, 500);
    };

    console.log('🔊 Attempting to play audio (normal mode)...');
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('✅ Audio playback started successfully');
        })
        .catch((error) => {
          console.error('❌ Error playing audio:', error);
          isPlayingAudioRef.current = false;
          currentAudioRef.current = null;
          processAudioQueue();
        });
    }
  };

  const playAudio = (audioBase64: string): void => {
    try {
      if (!audioBase64) {
        console.warn('No audio data provided');
        isPlayingAudioRef.current = false;
        processAudioQueue();
        return;
      }

      isPlayingAudioRef.current = true;

      // If recording is active, use Web Audio API to capture TTS in the recording
      if (
        isRecordingInterview &&
        audioContextRef.current &&
        mixedDestinationRef.current
      ) {
        console.log('🎙️ Recording active - routing TTS through Web Audio API');
        playAudioThroughWebAudio(audioBase64);
      } else {
        // Normal playback when not recording
        playAudioNormal(audioBase64);
      }
    } catch (error) {
      console.error('Error in playAudio:', error);
      isPlayingAudioRef.current = false;
      currentAudioRef.current = null;
      processAudioQueue();
    }
  };

  const sendLargeMessage = (
    messageType: string,
    data: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata: Record<string, any> = {}
  ): void => {
    const currentWs = wsRef.current || ws;
    if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const MAX_CHUNK_SIZE = 25000;
    const messageId =
      Date.now().toString() + Math.random().toString(36).substr(2, 9);

    const dataSize =
      typeof data === 'string' ? data.length : new Blob([data]).size;

    if (dataSize <= MAX_CHUNK_SIZE) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const message: Record<string, any> = {
        type: messageType,
        ...metadata,
        connection_id: connectionId,
      };

      if (messageType === 'architecture_image') {
        message.image_data = data;
      } else if (messageType === 'voice') {
        message.audio_data = data;
      } else if (messageType === 'screen') {
        message.image_data = data;
      } else if (messageType === 'text') {
        message.text = data;
      }

      if (currentWs && currentWs.readyState === WebSocket.OPEN) {
        currentWs.send(JSON.stringify(message));
      }
      return;
    }

    const chunks: string[] = [];
    for (let i = 0; i < data.length; i += MAX_CHUNK_SIZE) {
      chunks.push(data.slice(i, i + MAX_CHUNK_SIZE));
    }

    console.log(
      `Sending ${messageType} in ${chunks.length} chunks (total: ${dataSize} bytes)`
    );

    let chunksSent = 0;
    chunks.forEach((chunk, index) => {
      const chunkMessage = {
        type: 'chunk',
        message_type: messageType,
        message_id: messageId,
        chunk_index: index,
        total_chunks: chunks.length,
        chunk_data: chunk,
        ...metadata,
        connection_id: connectionId,
      };

      const chunkJson = JSON.stringify(chunkMessage);
      const chunkSize = new Blob([chunkJson]).size;

      if (chunkSize > 32000) {
        console.error(
          `❌ Chunk ${index} is still too large (${chunkSize} bytes), reducing chunk size`
        );
        return;
      }

      if (currentWs && currentWs.readyState === WebSocket.OPEN) {
        try {
          currentWs.send(chunkJson);
          chunksSent++;
          console.log(
            `📤 Sent chunk ${index + 1}/${
              chunks.length
            } for ${messageType} (${chunkSize} bytes, msgId: ${messageId.substring(
              0,
              8
            )}...)`
          );
        } catch (error) {
          console.error(
            `❌ Error sending chunk ${index} of ${messageType}:`,
            error
          );
          if (messageType === 'text' && addMessageRef.current) {
            addMessageRef.current(
              'error',
              'Failed to send message. Please try again.'
            );
          }
        }
      } else {
        console.error(
          `❌ Cannot send chunk ${index}: WebSocket not open (state: ${currentWs?.readyState})`
        );
        if (messageType === 'text' && addMessageRef.current) {
          addMessageRef.current(
            'error',
            'Connection lost. Please refresh the page.'
          );
        }
      }
    });

    console.log(
      `📤 ✅ All ${chunksSent}/${chunks.length} chunks sent for ${messageType} (messageId: ${messageId})`
    );
  };

  const sendVoiceData = async (audioBase64: string): Promise<void> => {
    interruptCurrentAudio();

    if (!audioBase64 || audioBase64.length === 0) {
      console.warn('⚠️ Empty audio data, not sending');
      return;
    }

    const currentWs = wsRef.current || ws;
    if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ WebSocket not ready, cannot send voice data');
      console.warn(
        '⚠️ WebSocket state:',
        currentWs?.readyState,
        'isConnected:',
        isConnected
      );
      if (addMessageRef.current) {
        addMessageRef.current(
          'error',
          'Connection lost. Please refresh the page.'
        );
      }
      return;
    }

    console.log(`🎤 Sending voice data: ${audioBase64.length} base64 chars`);
    console.log(
      `🎤 WebSocket state: ${currentWs.readyState}, connectionId: ${connectionId}`
    );
    console.log(`🎤 Environment: ${isDevelopment ? 'LOCAL' : 'AWS'}`);

    if (audioBase64.length <= 25000) {
      console.log('🎤 Audio small enough, sending directly via WebSocket');
      const message = {
        type: 'voice',
        audio_data: audioBase64,
        connection_id: connectionId,
      };
      currentWs.send(JSON.stringify(message));
      console.log(
        '🎤 Voice data sent directly, waiting for transcription response...'
      );
    } else if (isDevelopment) {
      console.log('🎤 Local dev: Using chunked WebSocket send');
      sendLargeMessage('voice', audioBase64);
      console.log(
        '🎤 Voice data sent (chunked), waiting for transcription response...'
      );
    } else {
      console.log(
        '🎤 AWS: Sending via HTTP POST to avoid Lambda chunking issues'
      );

      try {
        const token = getToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers['Authorization'] = `Basic ${token}`;
        }

        const userName = user?.username || user?.email || 'there';

        // OPTIMIZED: Single API call - transcribe and get AI response in one go
        // This eliminates one round trip, making the response faster
        console.log(
          '🎤 Calling /voice endpoint with audio_data (single API call for transcription + AI response)...'
        );
        const response = await fetch(`${API_URL}/voice`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            audio_data: audioBase64, // Backend will transcribe this and generate response
            connection_id: connectionId,
            interview_type: getCurrentInterviewType(),
            user_name: userName,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🎤 Voice API error:', response.status, errorText);
          if (addMessageRef.current) {
            addMessageRef.current(
              'error',
              `AI response failed: ${response.status}. ${errorText}`
            );
          }
          setIsAIResponding(false);
          return;
        }

        const result = (await response.json()) as VoiceResponse;
        console.log('🎤 Voice API response:', result);

        if (result.error) {
          console.error('🎤 Voice API returned error:', result.error);
          if (addMessageRef.current) {
            addMessageRef.current('error', result.error);
          }
          setIsAIResponding(false);
          return;
        }

        // Display transcript first (from single API call response)
        if (result.transcript && result.transcript.trim()) {
          if (addMessageRef.current) {
            addMessageRef.current('user', result.transcript);
            console.log('📝 Transcript displayed in chat from single API call');
          }
        } else {
          console.warn('🎤 No transcript in voice response');
        }

        // Show AI response
        if (result.response && result.response.trim()) {
          setIsAIResponding(false);
          addMessage('ai', result.response);
          console.log('🤖 AI response displayed in chat');
        } else {
          console.warn('🎤 No AI response in response');
          setIsAIResponding(false);
        }

        // Play TTS audio
        if (result.audio_data && result.audio_data.length > 0) {
          console.log('🔊 Playing TTS response...');
          audioQueueRef.current.push(result.audio_data);
          processAudioQueue();
        }

        console.log(
          '🎤 ✅ Voice processed successfully via HTTP (single API call - transcription + AI response)'
        );
      } catch (error) {
        console.error('🎤 Voice HTTP error:', error);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (addMessageRef.current) {
          addMessageRef.current(
            'error',
            `Voice processing error: ${errorMessage}`
          );
        }
        setIsAIResponding(false);

        console.log('🎤 Falling back to WebSocket chunking...');
        sendLargeMessage('voice', audioBase64);
      }
    }
  };

  const handleArchitectureImageUpload = (
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB');
        return;
      }

      const img = new Image();
      img.onload = (): void => {
        const canvas = document.createElement('canvas');
        const maxSize = 800;
        let width = img.width;
        let height = img.height;

        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);

          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
          const base64String = compressedDataUrl.split(',')[1];

          console.log(
            `Image compressed: ${file.size} bytes -> ~${base64String.length} base64 chars`
          );

          setArchitectureImageData(compressedDataUrl);
          sendArchitectureImage(base64String);
        }
      };

      const reader = new FileReader();
      reader.onloadend = (): void => {
        if (reader.result) {
          img.src = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendArchitectureImage = (imageBase64: string): void => {
    interruptCurrentAudio();

    if (addMessageRef.current) {
      addMessageRef.current(
        'user',
        "📤 I've uploaded an architecture diagram for review."
      );
    }

    const currentWs = wsRef.current || ws;
    if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
      alert('Not connected to interview session. Please wait for connection.');
      return;
    }

    if (!connectionId) {
      alert('Connection ID not available. Please refresh the page.');
      return;
    }

    try {
      const messageSize = new Blob([imageBase64]).size;
      console.log(`📤 Sending architecture image: ${messageSize} bytes`);

      if (messageSize > 500000) {
        alert('Image is too large. Please use a smaller image.');
        return;
      }

      sendLargeMessage('architecture_image', imageBase64);

      setHasArchitectureImage(true);
      console.log(
        '📤 Architecture image sent, waiting for backend confirmation...'
      );
    } catch (error) {
      console.error('❌ Error sending architecture image:', error);
      if (addMessageRef.current) {
        addMessageRef.current(
          'error',
          'Failed to upload architecture diagram. Please try again.'
        );
      }
    }
  };

  const sendScreenFrame = (imageBase64: string): void => {
    if (!imageBase64) {
      console.warn('⚠️ Screen frame data is null or undefined');
      return;
    }

    if (imageBase64.length < 100) {
      console.warn('⚠️ Invalid screen frame data, too small', {
        dataLength: imageBase64.length,
      });
      return;
    }

    const currentWs = wsRef.current || ws;
    if (!currentWs) {
      console.warn('⚠️ WebSocket not initialized, cannot send screen frame');
      return;
    }

    if (currentWs.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ WebSocket not ready, cannot send screen frame', {
        readyState: currentWs.readyState,
        readyStateText:
          currentWs.readyState === WebSocket.CONNECTING
            ? 'CONNECTING'
            : currentWs.readyState === WebSocket.CLOSING
            ? 'CLOSING'
            : currentWs.readyState === WebSocket.CLOSED
            ? 'CLOSED'
            : 'UNKNOWN',
      });

      if (currentWs.readyState === WebSocket.CLOSED) {
        console.log('🔄 WebSocket closed, attempting to reconnect...');
        setIsConnected(false);
        if (addMessageRef.current) {
          addMessageRef.current(
            'system',
            'Connection lost. Please refresh the page.'
          );
        }
      }
      return;
    }

    if (!connectionId) {
      console.warn('⚠️ No connection ID, cannot send screen frame');
      return;
    }

    try {
      // Include interview_type in metadata so Lambda can access it across invocations
      const currentInterviewType = getCurrentInterviewType();
      sendLargeMessage('screen', imageBase64, {
        interview_type: currentInterviewType,
        timestamp: Date.now(),
      });

      console.log('✅ Screen frame queued for sending');
    } catch (error) {
      console.error('❌ Error sending screen frame:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error('Error details:', {
        errorMessage,
        connectionId,
        dataLength: imageBase64.length,
      });

      if (errorMessage.includes('CLOSED')) {
        setIsConnected(false);
      }
    }
  };

  // Helper function to save feedback to backend with transcript
  const saveFeedbackToBackend = async (
    feedbackData: Feedback,
    includeTranscript: boolean = true
  ): Promise<void> => {
    const username = user?.username || user?.email?.split('@')[0] || 'unknown';

    try {
      const response = await fetch(`${API_URL}/feedbacks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${getToken()}`,
        },
        body: JSON.stringify({
          username,
          feedback: {
            score: feedbackData?.score || feedbackData?.overall_score || null,
            summary:
              feedbackData?.summary || feedbackData?.overall_assessment || null,
            improvements: feedbackData?.improvements || [],
            strengths: feedbackData?.strengths || [],
            speaking_clarity_analysis: feedbackData?.speaking_clarity_analysis ?? undefined,
            problem_solving_approach_score: feedbackData?.problem_solving_approach_score ?? undefined,
          },
          interviewType: config?.type,
          interviewName: config?.type
            ? config.type === 'coding'
              ? 'Live Coding'
              : config.type === 'system_design'
                ? 'System Design'
                : config.type === 'technical'
                  ? 'Technical Q&A'
                  : config.type === 'architecture_review'
                    ? 'Architecture Review'
                    : config.type === 'behavioural'
                      ? 'Behavioural'
                      : config.type === 'cv_review'
                        ? 'CV Review'
                        : config.type === 'focus_interview'
                          ? 'Focus Interview'
                          : config.type
            : null,
          messages: includeTranscript
            ? messages.map((msg) => ({
                sender: msg.sender,
                text: msg.text,
                timestamp:
                  msg.timestamp instanceof Date
                    ? msg.timestamp.toISOString()
                    : msg.timestamp,
              }))
            : null,
        }),
      });

      if (response.ok) {
        console.log('✅ Feedback saved to backend with transcript');
      } else {
        console.error('❌ Failed to save feedback:', response.status);
      }
    } catch (error) {
      console.error('❌ Error saving feedback:', error);
    }
  };

  const requestFeedback = (): void => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      if (addMessageRef.current) {
        addMessageRef.current(
          'error',
          'Not connected. Please wait for connection.'
        );
      }
      return;
    }

    if (messages.length === 0) {
      if (addMessageRef.current) {
        addMessageRef.current(
          'error',
          'No conversation to analyze. Please have a conversation first.'
        );
      }
      return;
    }

    setIsLoadingFeedback(true);
    setShowFeedbackModal(true);
    setFeedback(null);

    const conversationData = messages.map((msg) => ({
      sender: msg.sender,
      text: msg.text,
      timestamp: msg.timestamp,
    }));

    try {
      wsRef.current.send(
        JSON.stringify({
          type: 'request_feedback',
          connection_id: connectionId,
          conversation: conversationData,
          interview_type: getCurrentInterviewType(),
        })
      );
      console.log('📊 Feedback request sent');
    } catch (error) {
      console.error('Error sending feedback request:', error);
      if (addMessageRef.current) {
        addMessageRef.current(
          'error',
          'Failed to request feedback. Please try again.'
        );
      }
      setIsLoadingFeedback(false);
      setShowFeedbackModal(false);
    }
  };

  const sendTextMessage = (text: string): void => {
    if (!text || text.trim() === '') {
      return;
    }

    interruptCurrentAudio();

    addMessage('user', text);

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not ready, cannot send text message');
      return;
    }

    const message = {
      type: 'text',
      text: text,
      connection_id: connectionId,
    };
    const messageJson = JSON.stringify(message);
    const messageSize = new Blob([messageJson]).size;

    if (messageSize <= 25000) {
      try {
        wsRef.current.send(messageJson);
      } catch (error) {
        console.error('Error sending text message:', error);
        if (addMessageRef.current) {
          addMessageRef.current(
            'error',
            'Failed to send message. Please try again.'
          );
        }
      }
    } else {
      console.log(
        `Text message too large (${messageSize} bytes), using chunking`
      );
      sendLargeMessage('text', text);
    }
  };

  // Start camera preview (without recording)
  const startCameraPreview = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });

      interviewStreamRef.current = stream;
      // Set state first so video element renders, then useEffect will attach stream
      setIsCameraActive(true);
      console.log('📹 Camera preview started');
    } catch (error) {
      console.error('Error starting camera preview:', error);
      alert(
        'Failed to access camera. Please check your camera and microphone permissions.'
      );
    }
  };

  // Effect to attach stream to video element when camera becomes active
  useEffect(() => {
    if (
      isCameraActive &&
      interviewStreamRef.current &&
      videoPreviewRef.current
    ) {
      videoPreviewRef.current.srcObject = interviewStreamRef.current;
      videoPreviewRef.current.play().catch((err) => {
        console.warn('Could not auto-play video preview:', err);
      });
    }
  }, [isCameraActive]);

  // Stop camera preview
  const stopCameraPreview = (): void => {
    if (interviewStreamRef.current) {
      interviewStreamRef.current.getTracks().forEach((track) => track.stop());
      interviewStreamRef.current = null;
    }

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }

    setIsCameraActive(false);
    console.log('📹 Camera preview stopped');
  };

  const startInterviewRecording = async (): Promise<void> => {
    try {
      // If camera is not already active, start it
      if (!interviewStreamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        interviewStreamRef.current = stream;

        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
          videoPreviewRef.current.play().catch((err) => {
            console.warn('Could not auto-play video preview:', err);
          });
        }
        setIsCameraActive(true);
      }

      recordingChunksRef.current = [];

      // Set up AudioContext for mixing mic + AI TTS audio
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      // Create a destination node that will hold the mixed audio
      const mixedDestination = audioContext.createMediaStreamDestination();
      mixedDestinationRef.current = mixedDestination;

      // Connect microphone audio to the mixer
      const micAudioTrack = interviewStreamRef.current!.getAudioTracks()[0];
      if (micAudioTrack) {
        const micStream = new MediaStream([micAudioTrack]);
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(mixedDestination);
        micSourceRef.current = micSource;
        console.log('🎤 Microphone connected to audio mixer');
      }

      // Create a combined stream with video from camera + mixed audio
      const videoTrack = interviewStreamRef.current!.getVideoTracks()[0];
      const mixedAudioTrack = mixedDestination.stream.getAudioTracks()[0];

      const combinedStream = new MediaStream();
      if (videoTrack) {
        combinedStream.addTrack(videoTrack);
      }
      if (mixedAudioTrack) {
        combinedStream.addTrack(mixedAudioTrack);
      }

      console.log('🎬 Combined stream created:', {
        videoTracks: combinedStream.getVideoTracks().length,
        audioTracks: combinedStream.getAudioTracks().length,
      });

      // Create MediaRecorder with video support
      const mimeType = MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'video/webm'; // Fallback

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: mimeType,
      });

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (recordingChunksRef.current.length === 0) {
          console.error('❌ No recording chunks collected');
          alert(
            'Recording failed: No audio data was captured. Please try again.'
          );
          setIsRecordingInterview(false);
          return;
        }
        const blob = new Blob(recordingChunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          console.error('❌ Recording blob is empty');
          alert('Recording failed: Empty recording. Please try again.');
          setIsRecordingInterview(false);
          return;
        }
        setRecordedBlob(blob);
        console.log(
          '✅ Interview recording stopped, blob size:',
          blob.size,
          'bytes'
        );
      };

      mediaRecorder.onerror = (event: Event) => {
        console.error('❌ MediaRecorder error:', event);
        alert('Recording error occurred. Please try again.');
        setIsRecordingInterview(false);
        stopInterviewRecording();
      };

      interviewRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect chunks every second

      setIsRecordingInterview(true);
      setRecordingDuration(0);

      // Start duration timer
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      console.log('🎙️ Interview recording started');
    } catch (error) {
      console.error('Error starting interview recording:', error);
      alert(
        'Failed to start recording. Please check your microphone permissions.'
      );
    }
  };

  const stopInterviewRecording = (): void => {
    if (
      interviewRecorderRef.current &&
      interviewRecorderRef.current.state !== 'inactive'
    ) {
      interviewRecorderRef.current.stop();
    }

    // Clean up audio mixing context
    if (currentTtsSourceRef.current) {
      try {
        currentTtsSourceRef.current.stop();
      } catch {
        // Ignore if already stopped
      }
      currentTtsSourceRef.current = null;
    }
    if (micSourceRef.current) {
      micSourceRef.current.disconnect();
      micSourceRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    mixedDestinationRef.current = null;

    // Don't stop the camera stream - keep it for preview
    // User can manually turn off camera if they want

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecordingInterview(false);
    console.log('🛑 Interview recording stopped (camera still active)');
  };

  // Stop everything (camera + recording)
  const stopAllRecording = (): void => {
    stopInterviewRecording();
    stopCameraPreview();
  };

  const submitRecording = async (): Promise<void> => {
    if (!recordedBlob) {
      alert('No recording available. Please record an interview first.');
      return;
    }

    if (!user?.username) {
      alert('User not logged in. Please log in to submit recordings.');
      return;
    }

    if (recordedBlob.size === 0) {
      alert('Recording is empty. Please record again.');
      return;
    }

    console.log('📤 Submitting recording via presigned URL:', {
      size: recordedBlob.size,
      type: recordedBlob.type,
      duration: recordingDuration,
      username: user.username,
    });

    const interviewName =
      config.interviews && config.interviews.length > 0
        ? config.interviews.map((i) => i.name).join(' - ')
        : config.type || 'Interview';

    setIsSubmittingRecording(true);

    try {
      const token = await getToken();
      const mimeType = recordedBlob.type || 'video/webm';

      // Step 1: Get presigned upload URL
      console.log('📤 Step 1: Getting presigned upload URL...');
      const urlResponse = await fetch(
        `${RECORDINGS_API_URL}/recordings/get-upload-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            username: user.username,
            interviewName: interviewName,
            sessionId: config.sessionId,
            interviewType: config.type,
            mimeType: mimeType,
            fileSize: recordedBlob.size,
          }),
        }
      );

      if (!urlResponse.ok) {
        const errorText = await urlResponse.text();
        console.error('Failed to get upload URL:', errorText);
        throw new Error(`Failed to get upload URL: ${urlResponse.status}`);
      }

      const urlData = await urlResponse.json();
      console.log('📤 Got presigned URL:', {
        recordingId: urlData.recordingId,
        s3Key: urlData.s3Key,
      });

      // Step 2: Upload directly to S3 using presigned URL
      console.log('📤 Step 2: Uploading to S3 directly...');
      const s3Response = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': mimeType,
        },
        body: recordedBlob, // Send blob directly, no base64 encoding
      });

      if (!s3Response.ok) {
        console.error('S3 upload failed:', s3Response.status);
        throw new Error(`S3 upload failed: ${s3Response.status}`);
      }
      console.log('✅ S3 upload successful');

      // Step 3: Confirm upload and save metadata
      console.log('📤 Step 3: Confirming upload...');
      const confirmResponse = await fetch(
        `${RECORDINGS_API_URL}/recordings/confirm-upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            username: user.username,
            recordingId: urlData.recordingId,
            s3Key: urlData.s3Key,
            interviewName: interviewName,
            sessionId: config.sessionId,
            interviewType: config.type,
            mimeType: mimeType,
            fileSize: recordedBlob.size,
            duration: recordingDuration,
          }),
        }
      );

      if (!confirmResponse.ok) {
        let errorDetail = '';
        try {
          const errorJson = await confirmResponse.json();
          errorDetail =
            errorJson.detail || errorJson.message || JSON.stringify(errorJson);
        } catch {
          const errorText = await confirmResponse.text();
          errorDetail = errorText || `HTTP ${confirmResponse.status}`;
        }
        console.error('Confirm upload failed:', {
          status: confirmResponse.status,
          detail: errorDetail,
        });
        throw new Error(
          `Failed to confirm upload (${confirmResponse.status}): ${errorDetail}`
        );
      }

      const result = await confirmResponse.json();
      console.log('✅ Recording uploaded and confirmed:', result);
      alert('Recording saved successfully!');
      setRecordedBlob(null);
      setRecordingDuration(0);
    } catch (error) {
      console.error('Error submitting recording:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      // Check if it's the DynamoDB metadata error (backend needs deployment)
      const isMetadataError = errorMessage.includes(
        'Failed to save recording metadata'
      );
      const helpfulMessage = isMetadataError
        ? `${errorMessage}\n\n⚠️ The backend may need to be redeployed with the latest fixes.`
        : errorMessage;

      alert(
        `Failed to save recording: ${helpfulMessage}\n\nPlease check the console for details.`
      );
    } finally {
      setIsSubmittingRecording(false);
    }
  };

  const interruptAI = (): void => {
    interruptCurrentAudio();

    if (ws && ws.readyState === WebSocket.OPEN && isAIResponding) {
      ws.send(
        JSON.stringify({
          type: 'interrupt',
          connection_id: connectionId,
        })
      );
      setIsAIResponding(false);
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      isPlayingAudioRef.current = false;
      audioQueueRef.current = [];
    }
  };

  const handleEndInterview = async (): Promise<void> => {
    console.log('Ending interview...', { isMultiSession, currentStageIndex });

    interruptCurrentAudio();

    // If in a multi-session, show session summary modal with all collected feedbacks
    if (isMultiSession && config?.interviews && config.interviews.length > 0) {
      const currentFeedbacks =
        stageFeedbacksRef.current.length > 0
          ? stageFeedbacksRef.current
          : stageFeedbacks;

      console.log(
        '📊 Multi-session ending: Showing session summary modal with all feedbacks',
        {
          feedbackCount: currentFeedbacks.length,
          stageFeedbacksCount: stageFeedbacks.length,
        }
      );

      // Prepare session summary feedback with all collected stage feedbacks (including improvements and strengths)
      const sessionSummaryFeedback: Feedback = {
        isSessionSummary: true,
        stages: currentFeedbacks.map((f) => ({
          type: f.type || 'unknown',
          name: f.name || 'Unknown',
          score: f.score ?? undefined,
          summary: f.summary ?? undefined,
          improvements: f.improvements ?? (f.feedback?.improvements || []),
          strengths: f.strengths ?? (f.feedback?.strengths || []),
        })),
        totalScore:
          currentFeedbacks.length > 0
            ? Math.round(
                currentFeedbacks.reduce((sum, f) => sum + (f.score || 0), 0) /
                  currentFeedbacks.length
              )
            : null,
      };

      // Mark that we're ending the session - this flag will be checked when modal closes
      isEndingSessionRef.current = true;
      setSessionComplete(true);

      // Store session summary feedback in pendingStageFeedbackRef so close handler can detect it
      pendingStageFeedbackRef.current = sessionSummaryFeedback as Feedback;

      // Show session summary modal
      setFeedback(sessionSummaryFeedback);
      setShowFeedbackModal(true);

      console.log(
        '✅ Session summary modal shown. Waiting for user to close it to complete session and return home.'
      );
      return; // Exit - modal close handler will complete the session and return home
    }

    // Single interview - end immediately
    console.log('Ending interview and closing WebSocket connection...');

    const currentWs = wsRef.current || ws;
    if (currentWs) {
      try {
        // Clear keepalive interval
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const keepaliveInterval = (currentWs as any).__keepaliveInterval;
        if (keepaliveInterval) {
          clearInterval(keepaliveInterval);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          delete (currentWs as any).__keepaliveInterval;
        }

        currentWs.onopen = null;
        currentWs.onmessage = null;
        currentWs.onerror = null;
        currentWs.onclose = null;

        if (currentWs.readyState === WebSocket.OPEN) {
          console.log('Closing WebSocket connection (OPEN)...');
          // Send end_interview message to backend to clear state
          try {
            currentWs.send(
              JSON.stringify({
                type: 'end_interview',
                connection_id: connectionId,
              })
            );
            console.log('📤 Sent end_interview message to backend');
          } catch (e) {
            console.warn('Could not send end_interview message:', e);
          }
          currentWs.close(1000, 'Interview ended by user');
        } else if (currentWs.readyState === WebSocket.CONNECTING) {
          console.log('Aborting WebSocket connection (CONNECTING)...');
          currentWs.close();
        } else {
          console.log(
            `WebSocket state: ${currentWs.readyState}, cleaning up references`
          );
        }

        setTimeout(() => {
          if (currentWs.readyState !== WebSocket.CLOSED) {
            console.warn('WebSocket not closed, forcing cleanup');
            try {
              currentWs.close();
            } catch (e) {
              console.error('Error force-closing WebSocket:', e);
            }
          }
        }, 100);
      } catch (error) {
        console.error('Error closing WebSocket:', error);
      } finally {
        wsRef.current = null;
        setWs(null);
      }
    }

    // IMPORTANT: Clear all state when ending interview
    // Stop camera and recording if active
    if (isRecordingInterview || isCameraActive) {
      stopAllRecording();
    }
    setIsConnected(false);
    setIsRecording(false);
    setIsRecordingInterview(false);
    setIsCameraActive(false);
    setIsAIResponding(false);
    setConnectionId(null); // Reset connection ID
    setMessages([]); // Clear messages
    stageMessagesRef.current = []; // Clear stage messages
    setRecordedBlob(null);
    setRecordingDuration(0);
    setStageFeedbacks([]);
    stageFeedbacksRef.current = []; // Clear ref // Clear stage feedbacks
    stageFeedbacksRef.current = []; // Clear ref
    stageFeedbacksRef.current = []; // Clear ref
    pendingStageFeedbackRef.current = null; // Clear pending feedback
    isEndingSessionRef.current = false; // Reset ending flag
    isEndingSessionRef.current = false; // Reset ending flag

    audioQueueRef.current = [];
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    isPlayingAudioRef.current = false;

    console.log('Interview ended, calling onEnd callback');
    onEnd();
  };

  const displayType = isMultiSession
    ? getCurrentStage()?.name || 'Interview'
    : config?.type === 'technical'
    ? 'TECHNICAL Q&A'
    : (config?.type || '').replace('_', ' ').toUpperCase();

  const interviewType = getCurrentInterviewType();

  return (
    <div
      className={`flex flex-col h-screen min-h-0 overflow-x-hidden bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 text-slate-800 ${interviewType}`}
    >
      {/* Compact Header - responsive: wraps on small screens */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-3 sm:px-6 py-2 sm:py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          {/* Left: Title and status - can wrap on very small */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink">
            <h2 className="text-slate-800 text-sm sm:text-lg font-semibold m-0 truncate">
              AI Interview Practice
            </h2>
            <span className="bg-indigo-500 text-white px-2 py-0.5 rounded text-xs font-semibold capitalize flex-shrink-0">
              {displayType}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                isConnected
                  ? 'text-emerald-600 bg-emerald-50'
                  : 'text-red-600 bg-red-50'
              }`}
            >
              {isConnected ? '● Connected' : '○ Disconnected'}
            </span>
          </div>

          {/* Center: Multi-session progress (if applicable) */}
          {isMultiSession && (
            <div className="hidden md:flex items-center gap-3 order-3 w-full md:w-auto md:order-2">
              <div className="flex gap-1">
                {config?.interviews?.map((interview, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-center w-7 h-7 rounded-full border-2 transition-all ${
                      idx === currentStageIndex
                        ? 'bg-indigo-500 border-indigo-500 scale-110'
                        : idx < currentStageIndex
                        ? 'bg-emerald-500 border-emerald-500'
                        : 'bg-slate-100 border-slate-300'
                    }`}
                    title={interview.name}
                  >
                    <span
                      className={`text-sm ${
                        idx <= currentStageIndex ? 'brightness-0 invert' : ''
                      }`}
                    >
                      {interview.icon}
                    </span>
                  </div>
                ))}
              </div>
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded font-mono text-xs font-bold ${
                  stageTimeRemaining <= 30
                    ? 'bg-red-100 text-red-600 animate-pulse'
                    : stageTimeRemaining <= 60
                    ? 'bg-amber-100 text-amber-600'
                    : 'bg-emerald-100 text-emerald-600'
                }`}
              >
                ⏱️ {formatTime(stageTimeRemaining)}
              </div>
              {awaitingUserFinish && (
                <span className="bg-amber-100 text-amber-600 px-2 py-1 rounded text-xs animate-pulse">
                  Waiting...
                </span>
              )}
            </div>
          )}

          {/* Right: Action buttons - wrap on small, no truncation */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {!isMultiSession && (
              <button
                onClick={requestFeedback}
                className="text-xs px-2 sm:px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded transition-colors disabled:opacity-50 whitespace-nowrap"
                disabled={isLoadingFeedback || messages.length === 0}
                title="Get feedback"
              >
                {isLoadingFeedback ? '⏳...' : '📊 Feedback'}
              </button>
            )}
            {user && (
              <button
                onClick={signOut}
                className="text-xs px-2 sm:px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-600 rounded whitespace-nowrap"
                title={`Signed in as ${user.email || user.username}. Sign Out`}
              >
                Sign Out
              </button>
            )}
            <button
              onClick={handleEndInterview}
              className="px-2 sm:px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold whitespace-nowrap"
              title="End Interview"
            >
              <span className="sm:hidden">End</span>
              <span className="hidden sm:inline">End Interview</span>
            </button>
          </div>
        </div>
      </div>

      {/* Camera & Recording Bar - Pro only, below header */}
      {isPro && (
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-3 sm:px-4 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-slate-600 text-xs font-medium">Recording:</span>
          {!isCameraActive && !isRecordingInterview && (
            <button
              onClick={startCameraPreview}
              className="px-2 py-1 bg-slate-600 hover:bg-slate-700 text-white rounded text-xs font-medium transition-colors"
              title="Turn on camera"
            >
              📹 Camera
            </button>
          )}
          {isCameraActive && !isRecordingInterview && (
            <button
              onClick={stopCameraPreview}
              className="px-2 py-1 bg-slate-500 hover:bg-slate-600 text-white rounded text-xs font-medium transition-colors"
              title="Turn off camera"
            >
              📹 Off
            </button>
          )}
          {!isRecordingInterview && !recordedBlob && (
            <button
              onClick={startInterviewRecording}
              className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium transition-colors"
              title="Start recording"
            >
              🔴 Start Recording
            </button>
          )}
          {isRecordingInterview && (
            <>
              <div className="flex items-center gap-1 px-2 py-1 bg-red-100 rounded text-xs">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-red-700 font-mono">
                  {Math.floor(recordingDuration / 60)}:
                  {(recordingDuration % 60).toString().padStart(2, '0')}
                </span>
              </div>
              <button
                onClick={stopInterviewRecording}
                className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium"
                title="Stop recording"
              >
                ⏹️ Stop
              </button>
            </>
          )}
          {recordedBlob && !isRecordingInterview && (
            <>
              <button
                onClick={submitRecording}
                disabled={isSubmittingRecording}
                className="px-2 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded text-xs font-medium disabled:opacity-50"
                title="Save recording"
              >
                {isSubmittingRecording ? '⏳ Saving…' : '☁️ Save'}
              </button>
              <button
                onClick={() => {
                  setRecordedBlob(null);
                  setRecordingDuration(0);
                }}
                className="px-2 py-1 bg-slate-400 hover:bg-slate-500 text-white rounded text-xs"
                title="Discard recording"
              >
                🗑️ Discard
              </button>
            </>
          )}
          {/* Small camera preview thumbnail */}
          {(isCameraActive || isRecordingInterview) && (
            <div
              className={`relative w-16 h-12 rounded overflow-hidden bg-black flex-shrink-0 ml-auto ${
                isRecordingInterview
                  ? 'ring-2 ring-red-500'
                  : 'ring-1 ring-slate-300'
              }`}
              title="Camera preview"
            >
              <video
                ref={videoPreviewRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover transform scale-x-[-1]"
              />
              {isRecordingInterview && (
                <div className="absolute top-0 left-0 px-1 bg-red-600 text-white text-[8px] font-bold rounded-br">
                  REC
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden gap-3 p-3 sm:p-4 lg:p-6">
        <div className="flex-1 lg:flex-[2] flex flex-col gap-3 min-w-0 overflow-hidden flex-shrink">
          {getCurrentInterviewType() === 'coding' ? (
            <CodeEditor
              onCodeSubmit={(codeData: CodeData) => {
                console.log('Code submitted:', codeData);
                // Add the code to messages so it's included in feedback
                const codeMessage = `Here's my code:\n\`\`\`javascript\n${
                  codeData.currentCode
                }\n\`\`\`${
                  codeData.output ? `\n\nOutput:\n${codeData.output}` : ''
                }`;
                addMessage('user', codeMessage);
              }}
              onSendToAI={() => {
                // This is called after code is sent - placeholder message is handled in onCodeSubmit
              }}
              connectionId={connectionId}
              ws={wsRef.current || ws}
            />
          ) : getCurrentInterviewType() === 'architecture_review' ? (
            <div className="w-full h-full flex flex-col bg-white rounded-lg p-5 overflow-auto border border-slate-200 shadow-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-slate-800 m-0 font-semibold text-lg">
                  Architecture Diagram
                </h3>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleArchitectureImageUpload}
                  className="hidden"
                  id="architecture-image-upload"
                />
                <label
                  htmlFor="architecture-image-upload"
                  className={`px-4 py-2 text-white border-none rounded text-sm cursor-pointer transition-all duration-200 inline-block hover:-translate-y-0.5 ${
                    hasArchitectureImage
                      ? 'bg-emerald-500 hover:bg-emerald-600'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {hasArchitectureImage
                    ? '✓ Change Image'
                    : '📤 Upload Architecture Diagram'}
                </label>
              </div>
              {architectureImageData ? (
                <div className="flex-1 flex justify-center items-center bg-slate-50 rounded border border-slate-200 p-5 overflow-auto">
                  <img
                    src={architectureImageData}
                    alt="Architecture Diagram"
                    className="max-w-full max-h-full object-contain rounded"
                  />
                </div>
              ) : (
                <div className="flex-1 flex flex-col justify-center items-center bg-slate-50 rounded text-slate-500 text-center p-10 border border-slate-200">
                  <p className="text-lg mb-2.5 m-0">
                    No architecture diagram uploaded yet
                  </p>
                  <p className="text-sm m-0">
                    Click "Upload Architecture Diagram" to get started
                  </p>
                </div>
              )}
            </div>
          ) : (
            (getCurrentInterviewType() === 'coding' ||
              getCurrentInterviewType() === 'system_design' ||
              (getCurrentInterviewType() !== 'behavioural' &&
                getCurrentInterviewType() !== 'technical' &&
                windowWidth > 768)) && (
              <div className="flex-1 min-h-0 flex flex-col">
                <ScreenShare
                  ref={screenShareRef}
                  isSharing={isSharingScreen}
                  onShareChange={setIsSharingScreen}
                  onFrameCapture={sendScreenFrame}
                />
              </div>
            )
          )}

          <div className="flex-shrink-0">
            <VoiceRecorder
              isRecording={isRecording}
              isMuted={false}
              onMuteToggle={() => {
                setIsRecording((prev) => !prev);
              }}
              onAudioData={sendVoiceData}
              connectionId={connectionId}
              interviewType={getCurrentInterviewType()}
            />
          </div>
        </div>

        <div className="flex-1 min-w-0 lg:flex-[1] lg:min-w-[280px] lg:max-w-[500px] bg-white border border-slate-200 rounded-xl shadow-md flex flex-col overflow-hidden">
          <ChatDisplay
            messages={messages}
            onSendMessage={sendTextMessage}
            isAIResponding={isAIResponding}
            onInterrupt={interruptAI}
          />
        </div>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          console.log('🔘 Feedback modal close handler called', {
            isMultiSession,
            hasPendingFeedback: !!pendingStageFeedbackRef.current,
            isEndingSession: isEndingSessionRef.current,
            sessionComplete,
            feedbackIsSessionSummary: feedback?.isSessionSummary,
            feedbackStageName: feedback?.stageName,
            isTransitioning,
          });

          setShowFeedbackModal(false);

          // If we're ending the session (session summary modal was shown), save session and return home
          // Check both feedback state and pendingStageFeedbackRef to ensure we detect session summary correctly
          const storedFeedback = pendingStageFeedbackRef.current;
          const isSessionSummaryModal =
            feedback?.isSessionSummary === true ||
            (storedFeedback &&
              typeof storedFeedback === 'object' &&
              'isSessionSummary' in storedFeedback &&
              storedFeedback.isSessionSummary === true);

          if (isEndingSessionRef.current && isSessionSummaryModal) {
            console.log(
              '🏁 Session summary modal closed - saving session and returning home...'
            );
            isEndingSessionRef.current = false; // Reset flag

            // Clear pending feedback ref
            pendingStageFeedbackRef.current = null;

            // Close WebSocket
            const currentWs = wsRef.current || ws;
            if (currentWs) {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const keepaliveInterval = (currentWs as any)
                  .__keepaliveInterval;
                if (keepaliveInterval) {
                  clearInterval(keepaliveInterval);
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  delete (currentWs as any).__keepaliveInterval;
                }
                currentWs.onopen = null;
                currentWs.onmessage = null;
                currentWs.onerror = null;
                currentWs.onclose = null;
                if (currentWs.readyState === WebSocket.OPEN) {
                  currentWs.send(
                    JSON.stringify({
                      type: 'end_interview',
                      connection_id: connectionId,
                    })
                  );
                  currentWs.close(1000, 'Interview ended by user');
                }
              } catch (e) {
                console.warn('Error closing WebSocket after feedback:', e);
              } finally {
                wsRef.current = null;
                setWs(null);
              }
            }

            setIsConnected(false);
            setIsRecording(false);
            setIsAIResponding(false);
            setConnectionId(null);

            // Save session to backend, then return home
            setTimeout(async () => {
              console.log(
                '💾 Saving session after closing session summary modal',
                {
                  stageFeedbacksCount: stageFeedbacks.length,
                  stageFeedbacksFromRef: stageFeedbacksRef.current.length,
                }
              );

              if (completeSessionRef.current) {
                await completeSessionRef.current();
              } else {
                console.error('❌ completeSessionRef.current is null');
              }

              // Return to home page after session is saved
              setTimeout(() => {
                console.log('🏠 Returning to home page...');
                onEnd();
              }, 500); // Small delay to ensure session save completes
            }, 300); // Delay to ensure state updates
          }
          // If this was a stage feedback in multi-session, proceed to next stage after closing
          // pendingStageFeedbackRef is set when we receive feedback_response during a stage transition
          // If it exists, it means we need to proceed to the next stage
          // NOTE: This applies to timer-triggered feedback as well as manual feedback requests
          // IMPORTANT: If we're in a multi-session and have pending feedback, and it's NOT a session summary,
          // we should proceed to the next stage (regardless of how the modal was closed - X button or otherwise)
          // Check if this is a stage feedback (not session summary) - use stored feedback state before modal closes
          else if (
            isMultiSession &&
            pendingStageFeedbackRef.current &&
            !sessionComplete &&
            !isEndingSessionRef.current // Don't proceed if we're ending the session
          ) {
            // Check if this is a session summary by checking the stored feedback data
            const storedFeedback = pendingStageFeedbackRef.current;
            const isSessionSummary =
              feedback?.isSessionSummary === true ||
              (storedFeedback &&
                typeof storedFeedback === 'object' &&
                'isSessionSummary' in storedFeedback &&
                storedFeedback.isSessionSummary === true);

            // Also check if storedFeedback has isStageFeedback flag (explicit stage feedback marker)
            const isStageFeedback =
              (storedFeedback &&
                typeof storedFeedback === 'object' &&
                'isStageFeedback' in storedFeedback &&
                storedFeedback.isStageFeedback === true) ||
              isTransitioning; // Fallback: check isTransitioning state

            if (isSessionSummary) {
              console.log(
                'ℹ️ This is a session summary, not proceeding to next stage'
              );
              pendingStageFeedbackRef.current = null;
            } else if (!isStageFeedback) {
              console.log(
                'ℹ️ This is not a stage feedback, not proceeding to next stage',
                {
                  hasIsStageFeedback:
                    storedFeedback &&
                    typeof storedFeedback === 'object' &&
                    'isStageFeedback' in storedFeedback,
                  isTransitioning,
                }
              );
              pendingStageFeedbackRef.current = null;
            } else {
              const pendingFeedback = pendingStageFeedbackRef.current;
              // Clear the ref immediately so we don't proceed twice
              pendingStageFeedbackRef.current = null;

              console.log(
                '🔄 Stage feedback modal closed (X button clicked), proceeding to next stage automatically...',
                {
                  hasPendingFeedback: !!pendingFeedback,
                  currentStageIndex,
                  totalStages: config?.interviews?.length || 0,
                  feedbackStageName: feedback?.stageName,
                  isSessionSummary: feedback?.isSessionSummary,
                  isTransitioning,
                  storedFeedbackKeys:
                    storedFeedback && typeof storedFeedback === 'object'
                      ? Object.keys(storedFeedback)
                      : [],
                }
              );

              // Small delay to ensure modal closes smoothly before proceeding
              setTimeout(() => {
                if (proceedToNextStageRef.current) {
                  console.log('✅ Calling proceedToNextStage automatically...');
                  // Pass the original feedback data (not the wrapper with flags)
                  const feedbackData =
                    pendingFeedback &&
                    typeof pendingFeedback === 'object' &&
                    'score' in pendingFeedback
                      ? pendingFeedback
                      : null;
                  proceedToNextStageRef.current(feedbackData);
                } else {
                  console.error(
                    '❌ proceedToNextStageRef.current is null, cannot proceed to next stage'
                  );
                  // Fallback: clear transition state
                  setIsTransitioning(false);
                }
              }, 100);
            }
          } else {
            // Single interview feedback or session summary or other cases
            // Note: Feedback is already saved when received (in feedback_response handler)
            // No need to save again here

            console.log('ℹ️ Modal closed - not proceeding to next stage', {
              isMultiSession,
              hasPendingFeedback: !!pendingStageFeedbackRef.current,
              sessionComplete,
              isEndingSession: isEndingSessionRef.current,
              feedbackStageName: feedback?.stageName,
              isSessionSummary: feedback?.isSessionSummary,
            });

            // Clear pending feedback ref if we're not proceeding (unless we're ending)
            if (
              pendingStageFeedbackRef.current &&
              !isEndingSessionRef.current
            ) {
              pendingStageFeedbackRef.current = null;
            }
          }

          setFeedback(null);
        }}
        feedback={feedback}
        messages={messages}
        hideExport={!isPro}
      />
    </div>
  );
};

export default InterviewSession;
